import React, { useState } from 'react';
import {
  makeStyles,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import { transceive } from './actions';
import { byteToHexString } from './util';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  stepper: {
    backgroundColor: 'transparent',
    padding: theme.spacing(3, 0),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  warningCard: {
    backgroundColor: '#fff3cd',
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  successCard: {
    backgroundColor: '#d4edda',
    borderLeft: `4px solid ${theme.palette.success.main}`,
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  recoveryCodeInput: {
    fontSize: '1.5rem',
    letterSpacing: '0.2em',
    fontFamily: 'monospace',
  },
}));

export default function PinRecovery() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const device = useSelector(state => state.device);
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [pinType, setPinType] = useState('user');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [recoveryStatus, setRecoveryStatus] = useState(null);

  const steps = [
    t('pinRecovery.step1'),
    t('pinRecovery.step2'),
    t('pinRecovery.step3'),
    t('pinRecovery.step4'),
  ];

  // Check recovery code status
  const checkRecoveryStatus = async () => {
    if (!device) {
      enqueueSnackbar(t('pinRecovery.error.noDevice'), { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      // APDU: 00 FF 52 00 00
      const response = await dispatch(transceive('00FF520000'));

      if (response && response.length >= 4) {
        const sw = response.slice(-4);

        if (sw === '9000') {
          const status = parseInt(response.substring(0, 2), 16);
          const statusMap = {
            0: 'unused',
            1: 'used',
            2: 'locked',
          };
          setRecoveryStatus(statusMap[status] || 'unknown');
          setActiveStep(1);
        } else {
          enqueueSnackbar(t('pinRecovery.error.checkFailed'), { variant: 'error' });
        }
      }
    } catch (error) {
      console.error('Error checking recovery status:', error);
      enqueueSnackbar(t('pinRecovery.error.checkFailed'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Reset PIN using recovery code
  const resetPin = async () => {
    if (!device) {
      enqueueSnackbar(t('pinRecovery.error.noDevice'), { variant: 'error' });
      return;
    }

    // Validation
    if (recoveryCode.length !== 8) {
      enqueueSnackbar(t('pinRecovery.error.invalidCode'), { variant: 'error' });
      return;
    }

    if (newPin.length < 6 || newPin.length > 8) {
      enqueueSnackbar(t('pinRecovery.error.invalidPin'), { variant: 'error' });
      return;
    }

    if (newPin !== confirmPin) {
      enqueueSnackbar(t('pinRecovery.error.pinMismatch'), { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      // P1: 0x00 for user PIN, 0x01 for admin PIN
      const p1 = pinType === 'user' ? 0x00 : 0x01;

      // Convert recovery code and new PIN to bytes
      const recoveryBytes = new TextEncoder().encode(recoveryCode.toUpperCase());
      const pinBytes = new TextEncoder().encode(newPin);

      // APDU: 00 FF 51 [P1] [LC] [recovery_code] [new_pin]
      const dataField = new Uint8Array([...recoveryBytes, ...pinBytes]);
      const p1Hex = p1.toString(16).padStart(2, '0');
      const lcHex = dataField.length.toString(16).padStart(2, '0');
      const dataHex = byteToHexString(dataField);
      const apduHex = `00FF51${p1Hex}${lcHex}${dataHex}`;

      const response = await dispatch(transceive(apduHex));

      if (response && response.length >= 4) {
        const sw = response.slice(-4);

        if (sw === '9000') {
          enqueueSnackbar(t('pinRecovery.success'), { variant: 'success' });
          setActiveStep(3);
        } else if (sw === '6982') {
          enqueueSnackbar(t('pinRecovery.error.invalidCodeOrUsed'), { variant: 'error' });
        } else {
          enqueueSnackbar(t('pinRecovery.error.resetFailed'), { variant: 'error' });
        }
      }
    } catch (error) {
      console.error('Error resetting PIN:', error);
      enqueueSnackbar(t('pinRecovery.error.resetFailed'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      checkRecoveryStatus();
    } else if (activeStep === 2) {
      resetPin();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setPinType('user');
    setRecoveryCode('');
    setNewPin('');
    setConfirmPin('');
    setRecoveryStatus(null);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              {t('pinRecovery.welcome')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('pinRecovery.welcomeDesc')}
            </Typography>

            <Card className={`${classes.card} ${classes.warningCard}`}>
              <CardContent>
                <Typography variant="body2">
                  <WarningIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {t('pinRecovery.warning')}
                </Typography>
              </CardContent>
            </Card>

            <Typography variant="body2" color="textSecondary" paragraph>
              {t('pinRecovery.requirements')}
            </Typography>
            <ul>
              <li><Typography variant="body2">{t('pinRecovery.req1')}</Typography></li>
              <li><Typography variant="body2">{t('pinRecovery.req2')}</Typography></li>
              <li><Typography variant="body2">{t('pinRecovery.req3')}</Typography></li>
            </ul>
          </div>
        );

      case 1:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              {t('pinRecovery.statusCheck')}
            </Typography>

            {recoveryStatus && (
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="body1">
                    <strong>{t('pinRecovery.currentStatus')}:</strong>{' '}
                    {recoveryStatus === 'unused' && (
                      <span style={{ color: '#4caf50' }}>
                        ✅ {t('pinRecovery.status.unused')}
                      </span>
                    )}
                    {recoveryStatus === 'used' && (
                      <span style={{ color: '#ff9800' }}>
                        ⚠️ {t('pinRecovery.status.used')}
                      </span>
                    )}
                    {recoveryStatus === 'locked' && (
                      <span style={{ color: '#f44336' }}>
                        🔒 {t('pinRecovery.status.locked')}
                      </span>
                    )}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {recoveryStatus === 'unused' && (
              <Alert severity="success" style={{ marginTop: 16 }}>
                {t('pinRecovery.canProceed')}
              </Alert>
            )}

            {recoveryStatus === 'used' && (
              <Alert severity="warning" style={{ marginTop: 16 }}>
                {t('pinRecovery.codeUsed')}
              </Alert>
            )}

            {recoveryStatus === 'locked' && (
              <Alert severity="error" style={{ marginTop: 16 }}>
                {t('pinRecovery.codeLocked')}
              </Alert>
            )}
          </div>
        );

      case 2:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              {t('pinRecovery.enterDetails')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('pinRecovery.pinType')}</InputLabel>
                  <Select
                    value={pinType}
                    onChange={(e) => setPinType(e.target.value)}
                  >
                    <MenuItem value="user">{t('pinRecovery.userPin')}</MenuItem>
                    <MenuItem value="admin">{t('pinRecovery.adminPin')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label={t('pinRecovery.recoveryCode')}
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="ABCD1234"
                  inputProps={{
                    maxLength: 8,
                    className: classes.recoveryCodeInput,
                  }}
                  helperText={t('pinRecovery.codeHelper')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label={t('pinRecovery.newPin')}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  inputProps={{ maxLength: 8 }}
                  helperText={t('pinRecovery.pinHelper')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label={t('pinRecovery.confirmPin')}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value)}
                  inputProps={{ maxLength: 8 }}
                  error={confirmPin && newPin !== confirmPin}
                  helperText={
                    confirmPin && newPin !== confirmPin
                      ? t('pinRecovery.error.pinMismatch')
                      : ''
                  }
                />
              </Grid>
            </Grid>

            <Card className={`${classes.card} ${classes.warningCard}`} style={{ marginTop: 24 }}>
              <CardContent>
                <Typography variant="body2">
                  <WarningIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {t('pinRecovery.resetWarning')}
                </Typography>
              </CardContent>
            </Card>
          </div>
        );

      case 3:
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckCircleIcon style={{ fontSize: 80, color: '#4caf50' }} />
            <Typography variant="h5" style={{ marginTop: 20, marginBottom: 10 }}>
              {t('pinRecovery.complete')}
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              {t('pinRecovery.completeDesc')}
            </Typography>

            <Card className={`${classes.card} ${classes.warningCard}`} style={{ marginTop: 24 }}>
              <CardContent>
                <Typography variant="body2">
                  <strong>{t('pinRecovery.nextSteps')}:</strong>
                </Typography>
                <ul style={{ textAlign: 'left', marginTop: 8 }}>
                  <li><Typography variant="body2">{t('pinRecovery.next1')}</Typography></li>
                  <li><Typography variant="body2">{t('pinRecovery.next2')}</Typography></li>
                  <li><Typography variant="body2">{t('pinRecovery.next3')}</Typography></li>
                </ul>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Container className={classes.container} maxWidth="md">
      <Paper className={classes.paper}>
        <Typography variant="h4" className={classes.title}>
          <LockOpenIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          {t('pinRecovery.title')}
        </Typography>

        <Stepper activeStep={activeStep} className={classes.stepper}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <div style={{ marginTop: 24 }}>
          {renderStepContent(activeStep)}
        </div>

        <div className={classes.buttonGroup}>
          {activeStep > 0 && activeStep < 3 && (
            <Button onClick={handleBack} disabled={loading}>
              {t('common.back')}
            </Button>
          )}
          <div style={{ flex: 1 }} />
          {activeStep < 3 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={loading || !device || (activeStep === 1 && recoveryStatus !== 'unused')}
              startIcon={loading ? <CircularProgress size={20} /> : <VpnKeyIcon />}
            >
              {activeStep === 2 ? t('pinRecovery.resetPin') : t('common.next')}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleReset}
            >
              {t('pinRecovery.resetAnother')}
            </Button>
          )}
        </div>
      </Paper>

      {/* Help Section */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          ❓ {t('pinRecovery.help')}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          <strong>{t('pinRecovery.q1')}</strong><br />
          {t('pinRecovery.a1')}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          <strong>{t('pinRecovery.q2')}</strong><br />
          {t('pinRecovery.a2')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>{t('pinRecovery.q3')}</strong><br />
          {t('pinRecovery.a3')}
        </Typography>
      </Paper>
    </Container>
  );
}
